/**
 * Owns MPV's right-side sidebar state: which tab is open, whether it's
 * expanded, and whether the map legend is collapsed. Also exposes
 * `openDetailsSidebarAndScrollToLegend` for the legend-link path.
 */

import * as I from 'immutable';
import queryString from 'query-string';
import React from 'react';
import {NavigationType, useLocation, useNavigate, useNavigationType} from 'react-router-dom';

import {PROPERTY_DETAILS_LAYER_LEGEND_SECTION_ID} from 'app/components/PropertyDetailsSidebar/constants';
import {
  DEFAULT_SIDEBAR_VIEW_STATE,
  SidebarViewState,
} from 'app/components/TabbedSidebar/TabbedSidebar';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {NotesState} from 'app/stores/NotesStore';
import * as routeUtils from 'app/utils/routeUtils';

const DETAILS_SIDEBAR_TITLE = 'Details';
const AGENT_SIDEBAR_TITLE = 'Agent';
const CHAT_SIDEBAR_TITLE = 'Chat';

function hasOpenChatParam(search: string): boolean {
  return queryString.parse(search)[routeUtils.OPEN_CHAT_QUERY_PARAM] !== undefined;
}

function hasOpenChatState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[routeUtils.OPEN_CHAT_STATE_KEY] === true
  );
}

function removeOpenChatFromSearch(search: string): string {
  const params = queryString.parse(search);
  delete params[routeUtils.OPEN_CHAT_QUERY_PARAM];
  const next = queryString.stringify(params);
  return next ? `?${next}` : '';
}

/**
 * Scrolls the details sidebar to the layer-legend section. The element only
 * exists while the Details tab is mounted; if it's not yet in the DOM (the
 * panel is still rendering after a tab switch), watch for it via
 * MutationObserver and scroll once it appears. Bounded by a 4-second
 * timeout so we don't observe forever if something goes wrong upstream.
 */
function scrollDetailsSidebarToLegend() {
  const existing = document.getElementById(PROPERTY_DETAILS_LAYER_LEGEND_SECTION_ID);
  if (existing) {
    existing.scrollIntoView({behavior: 'smooth', block: 'start'});
    return;
  }
  const observer = new MutationObserver(() => {
    const el = document.getElementById(PROPERTY_DETAILS_LAYER_LEGEND_SECTION_ID);
    if (el) {
      el.scrollIntoView({behavior: 'smooth', block: 'start'});
      observer.disconnect();
      window.clearTimeout(fallbackTimeoutId);
    }
  });
  observer.observe(document.body, {childList: true, subtree: true});
  const fallbackTimeoutId = window.setTimeout(() => observer.disconnect(), 4000);
}

interface Args {
  notesState: NotesState;
  selectedFeature: I.ImmutableOf<ApiFeature> | null;
}

export interface TabbedSidebar {
  tabbedSidebarState: SidebarViewState;
  setTabbedSidebarState: React.Dispatch<React.SetStateAction<SidebarViewState>>;
  isLegendCollapsed: boolean;
  setIsLegendCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  openDetailsSidebarAndScrollToLegend: () => void;
}

export default function useTabbedSidebarState({notesState, selectedFeature}: Args): TabbedSidebar {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const agentRunIdFromUrl = routeUtils.getStringParamFromQueryParams(
    queryString.parse(location.search),
    routeUtils.AGENT_RUN_ID_QUERY_PARAM
  );
  // A navigation the agent chat makes carries OPEN_CHAT_STATE (see
  // useChatMapControls). Only the push that makes it opens Chat. Back to that
  // entry is a POP, and a later replace that keeps the state (useSceneUrlState
  // does) is a REPLACE, so neither reopens the tab once the user has moved on.
  const chatNavigationArrived =
    navigationType === NavigationType.Push && hasOpenChatState(location.state);

  // An agent run link (?agentRunId=, see AgentExplorer) opens the Agent tab
  // directly; a focused note (#note-<id>) opens the sidebar on the default tab
  // and useNoteUrlState switches to the right tab once the note resolves.
  const [tabbedSidebarState, setTabbedSidebarState] = React.useState<SidebarViewState>(() => {
    if (agentRunIdFromUrl) {
      return {isExpanded: true, currentViewTitle: AGENT_SIDEBAR_TITLE};
    }
    if (chatNavigationArrived || hasOpenChatParam(location.search)) {
      return {isExpanded: true, currentViewTitle: CHAT_SIDEBAR_TITLE};
    }
    return notesState.focusedNoteId
      ? {...DEFAULT_SIDEBAR_VIEW_STATE, isExpanded: true}
      : DEFAULT_SIDEBAR_VIEW_STATE;
  });
  const [isLegendCollapsed, setIsLegendCollapsed] = React.useState<boolean>(false);

  // Drop the agentRunId deep-link param whenever the Agent tab isn't showing
  // (tab switch or sidebar collapse), so it doesn't linger and reopen the
  // Agent tab on a later unrelated navigation.
  const isAgentTabActive =
    tabbedSidebarState.isExpanded && tabbedSidebarState.currentViewTitle === AGENT_SIDEBAR_TITLE;

  // The run id the sidebar state below was last opened for. A param is only
  // stale once its tab has actually opened: both effects run in the same pass
  // when the link arrives, and the sidebar state the stripping one reads is
  // still the pre-link one, so it would otherwise strip the param before
  // AgentExplorer ever sees it.
  const [openedAgentRunId, setOpenedAgentRunId] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!agentRunIdFromUrl) {
      setOpenedAgentRunId(undefined);
      return;
    }
    setOpenedAgentRunId(agentRunIdFromUrl);
    setTabbedSidebarState({isExpanded: true, currentViewTitle: AGENT_SIDEBAR_TITLE});
    if (hasOpenChatParam(location.search)) {
      navigate(`${location.pathname}${removeOpenChatFromSearch(location.search)}${location.hash}`, {
        replace: true,
      });
    }
  }, [agentRunIdFromUrl, location, navigate]);

  React.useEffect(() => {
    if (isAgentTabActive || openedAgentRunId !== agentRunIdFromUrl) {
      return;
    }
    const nextSearch = routeUtils.removeAgentRunIdFromSearch(location.search);
    if (nextSearch !== location.search) {
      navigate(`${location.pathname}${nextSearch}${location.hash}`, {replace: true});
    }
  }, [isAgentTabActive, agentRunIdFromUrl, openedAgentRunId, location, navigate]);

  // A navigation from the chat also opens the tab when the page is already
  // mounted and only the route changed. Keyed on the entry, so each one opens
  // it, even after the user switched to another tab.
  React.useEffect(() => {
    if (!chatNavigationArrived || agentRunIdFromUrl) {
      return;
    }
    setTabbedSidebarState({isExpanded: true, currentViewTitle: CHAT_SIDEBAR_TITLE});
  }, [chatNavigationArrived, agentRunIdFromUrl, location.key]);

  // A URL from outside the app can carry ?chat=open. It opens the Chat tab and
  // is consumed at once so it can't reopen the tab later.
  React.useEffect(() => {
    if (!hasOpenChatParam(location.search) || agentRunIdFromUrl) {
      return;
    }
    setTabbedSidebarState({isExpanded: true, currentViewTitle: CHAT_SIDEBAR_TITLE});
    navigate(`${location.pathname}${removeOpenChatFromSearch(location.search)}${location.hash}`, {
      replace: true,
    });
  }, [agentRunIdFromUrl, location, navigate]);

  const openDetailsSidebarAndScrollToLegend = React.useCallback(() => {
    if (!selectedFeature) {
      return;
    }
    setTabbedSidebarState((prev) => ({
      ...prev,
      currentViewTitle: DETAILS_SIDEBAR_TITLE,
      isExpanded: true,
    }));
    setIsLegendCollapsed(true);
    // Wait for the sidebar expand animation to start before scrolling, so
    // scrollIntoView lands against the post-animation layout.
    window.setTimeout(() => scrollDetailsSidebarToLegend(), 350);
  }, [selectedFeature]);

  return {
    tabbedSidebarState,
    setTabbedSidebarState,
    isLegendCollapsed,
    setIsLegendCollapsed,
    openDetailsSidebarAndScrollToLegend,
  };
}
